import { useEffect } from "react";
import { format, parse, setHours, setMinutes } from "date-fns";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTE_STEP = 5;

type Meridiem = "AM" | "PM";
type TimeParts = { hour12: number; minute: number; meridiem: Meridiem };

function minuteOptions(minuteStep: number): number[] {
  return Array.from(
    { length: Math.ceil(60 / minuteStep) },
    (_, i) => i * minuteStep,
  ).filter((m) => m < 60);
}

/**
 * Parse a 24-hour "HH:MM" string into 12-hour parts. The minute is floored to
 * the nearest configured step — flooring (not rounding) so a normalized value
 * can never move forward in time, which would otherwise risk tripping the
 * backend's "contact_at must not be in the future" guard when the seed value is
 * `now`.
 */
function parseValue(value: string, minuteStep: number): TimeParts | null {
  if (!value) return null;
  const d = parse(value, "HH:mm", new Date());
  if (Number.isNaN(d.getTime())) return null;
  const hour24 = d.getHours();
  return {
    hour12: hour24 % 12 === 0 ? 12 : hour24 % 12,
    minute: d.getMinutes() - (d.getMinutes() % minuteStep),
    meridiem: hour24 < 12 ? "AM" : "PM",
  };
}

function toHour24(hour12: number, meridiem: Meridiem): number {
  if (meridiem === "AM") {
    return hour12 === 12 ? 0 : hour12;
  }
  return hour12 === 12 ? 12 : hour12 + 12;
}

/** Build a 24-hour "HH:MM" string from 12-hour parts. */
function toValue({ hour12, minute, meridiem }: TimeParts): string {
  const hour24 = toHour24(hour12, meridiem);
  return format(setMinutes(setHours(new Date(), hour24), minute), "HH:mm");
}

/**
 * 12-hour time picker (hour + minute + AM/PM). The value contract is a
 * 24-hour "HH:MM" string in and out, so it is a drop-in for `<input
 * type="time">` without touching any submit/combine logic. Hour/minute use
 * shadcn `Select` (portaled) — they rely on Select stacking above Popover
 * (see select.tsx z-index note); AM/PM is an inline ToggleGroup.
 */
export function TimePicker12({
  value,
  onChange,
  className,
  minuteStep = MINUTE_STEP,
  disabled = false,
}: {
  /** 24-hour "HH:MM" (or "" for empty). */
  value: string;
  /** Called with the next 24-hour "HH:MM" value. */
  onChange: (next: string) => void;
  className?: string;
  /** Minute grid. Defaults to 5 minutes; pass 1 for exact minute filtering. */
  minuteStep?: number;
  disabled?: boolean;
}) {
  const normalizedMinuteStep = Math.max(1, Math.min(60, Math.floor(minuteStep)));
  const minutes = minuteOptions(normalizedMinuteStep);
  const parts = parseValue(value, normalizedMinuteStep);

  // Normalize an off-grid incoming value (e.g. now = "17:43" → "17:40") once,
  // so the displayed time always equals the value that gets submitted.
  useEffect(() => {
    const p = parseValue(value, normalizedMinuteStep);
    if (!p) return;
    const normalized = toValue(p);
    if (normalized !== value) onChange(normalized);
  }, [value, onChange, normalizedMinuteStep]);

  const update = (patch: Partial<TimeParts>) => {
    const base: TimeParts = parts ?? { hour12: 12, minute: 0, meridiem: "AM" };
    onChange(toValue({ ...base, ...patch }));
  };

  // An empty value is "" rather than undefined: undefined would turn the controls uncontrolled, and
  // a cleared time would keep showing the old hour, minute and AM/PM.
  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      <Select
        value={parts ? String(parts.hour12) : ""}
        onValueChange={(v) => update({ hour12: Number(v) })}
        disabled={disabled}
      >
        <SelectTrigger size="sm" className="w-[4.5rem]" aria-label="Hour">
          <SelectValue placeholder="Hr" />
        </SelectTrigger>
        <SelectContent>
          {HOURS_12.map((h) => (
            <SelectItem key={h} value={String(h)}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground" aria-hidden="true">
        :
      </span>
      <Select
        value={parts ? String(parts.minute) : ""}
        onValueChange={(v) => update({ minute: Number(v) })}
        disabled={disabled}
      >
        <SelectTrigger size="sm" className="w-[4.5rem]" aria-label="Minute">
          <SelectValue placeholder="Min" />
        </SelectTrigger>
        <SelectContent>
          {minutes.map((m) => (
            <SelectItem key={m} value={String(m)}>
              {String(m).padStart(2, "0")}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ToggleGroup
        type="single"
        size="sm"
        variant="outline"
        value={parts?.meridiem ?? ""}
        onValueChange={(v) => v && update({ meridiem: v as Meridiem })}
        aria-label="AM or PM"
        disabled={disabled}
      >
        <ToggleGroupItem value="AM" aria-label="AM">
          AM
        </ToggleGroupItem>
        <ToggleGroupItem value="PM" aria-label="PM">
          PM
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
}
